/**
 * Autore: Salvo de Luca
 * Azienda: Dialogicom s.r.l.s.
 * File: src/components/modular-form/custom/GoogleMapsAutocomplete.tsx
 * Release: 5.0.0 - PlaceAutocompleteElement per nuove API keys
 * Descrizione: Componente per autocomplete indirizzi con PlaceAutocompleteElement
 */

import React, { useState, useRef, useEffect } from 'react';

interface GoogleMapsAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onPlaceSelect?: (place: any) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

const GoogleMapsAutocomplete: React.FC<GoogleMapsAutocompleteProps> = ({
  value,
  onChange,
  onPlaceSelect,
  placeholder = "Cerca indirizzo...",
  className = "",
  disabled = false
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false);
  const [elementCreated, setElementCreated] = useState(false);

  // Verifica caricamento Google Maps e Extended Component Library
  useEffect(() => {
    let attemptCount = 0;
    const maxAttempts = 20; // 4 secondi massimo (20 * 200ms)
    
    const checkGoogleMaps = () => {
      attemptCount++;
      
      if (typeof window !== 'undefined' && window.google && window.google.maps && window.google.maps.places) {
        // Log rimosso per pulizia console
        
        // Verifica se PlaceAutocompleteElement è disponibile
        if (customElements.get('gmpx-place-autocomplete')) {
          setIsGoogleMapsLoaded(true);
          return;
        } else if (attemptCount >= maxAttempts) {
          setIsGoogleMapsLoaded(true);
          return;
        } else {
          setTimeout(checkGoogleMaps, 200);
          return;
        }
      }
      
      if (attemptCount >= maxAttempts) {
        setIsGoogleMapsLoaded(true);
        return;
      }
      
      setTimeout(checkGoogleMaps, 100);
    };

    checkGoogleMaps();
  }, []);

  // Cleanup effect
  useEffect(() => {
    return () => {
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
        setElementCreated(false);
      }
    };
  }, []);

  // Inizializza PlaceAutocompleteElement quando Google Maps è pronto
  useEffect(() => {
    if (!isGoogleMapsLoaded || !containerRef.current || disabled || elementCreated) {
      return;
    }

    // Controlla se PlaceAutocompleteElement è disponibile
    const hasPlaceAutocompleteElement = customElements.get('gmpx-place-autocomplete');
    
    if (hasPlaceAutocompleteElement) {
      createPlaceAutocompleteElement();
    } else {
      createTraditionalAutocomplete();
    }
  }, [isGoogleMapsLoaded, disabled, placeholder, onChange, onPlaceSelect, elementCreated]);

  // Crea PlaceAutocompleteElement
  const createPlaceAutocompleteElement = () => {
    if (!containerRef.current) return;

    try {
      // Crea elemento HTML personalizzato
      const autocompleteElement = document.createElement('gmpx-place-autocomplete');
      
      // Configura proprietà
      autocompleteElement.setAttribute('type', 'address');
      autocompleteElement.setAttribute('country', 'IT');
      autocompleteElement.setAttribute('placeholder', placeholder);
      
      // Stili inline per matching del tema
      autocompleteElement.style.cssText = `
        width: 100%;
        --gmpx-color-surface: #27272a;
        --gmpx-color-on-surface: #ffffff;
        --gmpx-color-on-surface-variant: #a1a1aa;
        --gmpx-color-primary: #3b82f6;
        --gmpx-color-outline: #52525b;
        --gmpx-border-radius: 0.375rem;
        --gmpx-font-size: 0.875rem;
      `;

      // Listener per selezione place
      autocompleteElement.addEventListener('gmpx-placechange', (event: any) => {
        const place = event.detail.place;
        
        if (!place || !place.geometry) {
          return;
        }

        // Aggiorna il campo con l'indirizzo formattato
        if (place.formattedAddress) {
          onChange(place.formattedAddress);
        }

        // Notifica il parent component
        if (onPlaceSelect) {
          onPlaceSelect(place);
        }
      });

      // Aggiungi elemento al container
      containerRef.current.appendChild(autocompleteElement);
      setElementCreated(true);

    } catch (error) {
      console.error('[GoogleMapsAutocomplete] Errore creazione PlaceAutocompleteElement:', error);
      console.log('[GoogleMapsAutocomplete] Fallback su API tradizionale');
      createTraditionalAutocomplete();
    }
  };

  // Fallback: Autocomplete tradizionale
  const createTraditionalAutocomplete = () => {
    if (!containerRef.current) return;

    try {
      // Crea input per autocomplete tradizionale
      const input = document.createElement('input');
      input.type = 'text';
      input.placeholder = placeholder;
      input.value = value;
      input.className = `w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 transition-colors
        ${disabled 
          ? 'bg-zinc-800 !border-zinc-700 text-zinc-500 cursor-not-allowed' 
          : 'bg-zinc-800 !border-zinc-600 text-white placeholder-zinc-400 focus:!ring-zinc-500 focus:!border-zinc-500 hover:!border-zinc-500'
        }`;

      // Crea istanza Autocomplete
      const autocomplete = new google.maps.places.Autocomplete(input, {
        types: ['address'],
        componentRestrictions: { country: 'IT' },
        fields: ['formatted_address', 'address_components', 'geometry']
      });

      // Listener per selezione place
      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        
        if (!place.geometry) {
          return;
        }

        if (place.formatted_address) {
          onChange(place.formatted_address);
        }

        if (onPlaceSelect) {
          onPlaceSelect(place);
        }
      });

      // Listener per input manuale
      input.addEventListener('input', (e) => {
        onChange((e.target as HTMLInputElement).value);
      });

      containerRef.current.appendChild(input);
      setElementCreated(true);
      
    } catch (error) {
      console.error('[GoogleMapsAutocomplete] Errore creazione Autocomplete tradizionale:', error);
    }
  };

  return (
    <div className="w-full">
      <div 
        ref={containerRef} 
        className={`w-full ${className}`}
      />
      
      {!isGoogleMapsLoaded && (
        <p className="text-xs text-zinc-500 mt-1">Caricamento autocomplete Google Maps...</p>
      )}
      
      {isGoogleMapsLoaded && !elementCreated && (
        <p className="text-xs text-blue-500 mt-1">Inizializzazione autocomplete...</p>
      )}
    </div>
  );
};

export default GoogleMapsAutocomplete; 